export const normalizeType = (deviceOrType) => {
  const raw =
    typeof deviceOrType === "object"
      ? deviceOrType?.sensorType || deviceOrType?.type || deviceOrType?.deviceType
      : deviceOrType;

  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "_");
};

const deviceTypeAliases = {
  vibraton: "vibration",
  vibration_sensor: "vibration",
  humidity_sensor: "humidity",
  temperature_sensor: "temperature",
  microphone_sensor: "microphone",
  microphe: "microphone",
};

export const formatDeviceType = (value) => {
  const normalizedType = normalizeType(value);
  const type = deviceTypeAliases[normalizedType] || normalizedType;
  if (!type) return "Device";

  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\bGps\b/g, "GPS")
    .replace(/\bRfid\b/g, "RFID")
    .replace(/\bCctv\b/g, "CCTV")
    .replace(/\bTv\b/g, "TV");
};

const normalizeState = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getWarningMarginValue = (device = {}) => {
  const margin = toNumber(device.warningMargin);
  return margin !== null && margin >= 0 ? margin : 5;
};

const isInactiveByTime = (device = {}) => {
  const timeoutMinutes = toNumber(device.deviceInactiveMinutes) ?? 10;
  const recordedAt = device.recordedAt
    ? new Date(device.recordedAt).getTime()
    : null;
  if (!recordedAt || Number.isNaN(recordedAt)) return false;
  return Date.now() - recordedAt > timeoutMinutes * 60 * 1000;
};

export const isNonSensorDevice = (device = {}) => {
  const deviceType = normalizeState(device.deviceType);
  return deviceType && deviceType !== "SENSOR" && !device.sensorType;
};

export const isOnOffDevice = (device = {}) => {
  const type = normalizeType(device);
  return [
    "gps",
    "gps_tracker",
    "cctv",
    "camera",
    "dash_camera",
    "speaker",
    "rfid",
    "rfid_reader",
    "television",
    "efd",
  ].some((keyword) => type.includes(keyword));
};

export const isInactiveState = (device = {}) => {
  const states = [
    device.health,
    device.status,
    device.workingCondition,
    device.connectionStatus,
  ].map(normalizeState);

  return states.some((state) =>
    ["INACTIVE", "OFFLINE", "OFF", "NOT_WORKING", "NEEDS_REPAIR", "DEAD"].includes(state),
  ) || isInactiveByTime(device);
};

export const isSensorAlert = (device = {}) => {
  const states = [device.health, device.status, device.workingCondition].map(normalizeState);
  if (states.some((state) => ["ALERT", "CRITICAL", "NOT_WORKING"].includes(state))) {
    return true;
  }

  const value = toNumber(device.value);
  const min = toNumber(device.min ?? device.minValue);
  const max = toNumber(device.max ?? device.maxValue);
  return value !== null && ((min !== null && value < min) || (max !== null && value > max));
};

export const isSensorNearThreshold = (device = {}) => {
  const states = [device.health, device.status].map(normalizeState);
  if (states.some((state) => state === "WARNING")) return true;
  if (isSensorAlert(device)) return false;

  const value = toNumber(device.value);
  const min = toNumber(device.min ?? device.minValue);
  const max = toNumber(device.max ?? device.maxValue);
  if (value === null || min === null || max === null || max <= min) return false;

  const warningDistance = getWarningMarginValue(device);
  return value - min <= warningDistance || max - value <= warningDistance;
};
